import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import { Dismiss24Regular, DocumentArrowDownRegular } from '@fluentui/react-icons';
import { useEffect, useState } from 'react';
import { ButtonLoading, OptionSwatch } from '@shared/components';
import { TVersionPreferenceDialogState, TVersionPreference } from '@modules/items';
import { useBulkExport } from '../../api/bulkExports.api';
import { useBulkExportSubmission } from '@modules/submissions';

type TProps = {
  state?: TVersionPreferenceDialogState;
  setState: (state: TVersionPreferenceDialogState) => void;
};

const VersionPreferenceDialog = ({ state, setState }: TProps) => {
  const [selectedMode, setSelectedMode] = useState<TVersionPreference>('DRAFT');

  useEffect(() => {
    if (state?.isOpen) setSelectedMode(state.allowedPreferences?.[0] || 'DRAFT');
  }, [state?.allowedPreferences, state?.isOpen]);

  const bulkExport = useBulkExport();
  const bulkExportSubmission = useBulkExportSubmission();
  const handleSelect = () => {
    if (!state) return;
    state.onSelect(selectedMode);

    setState({ ...state, isOpen: false });
  };

  return (
    <Dialog
      open={state?.isOpen || false}
      onOpenChange={(_, data) => {
        if (state) setState({ ...state, isOpen: data.open });
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            Select version preference
          </DialogTitle>
          <DialogContent>
            {state?.message && (
              <MessageBar intent="warning" className="mb-2">
                <MessageBarBody>{state.message}</MessageBarBody>
              </MessageBar>
            )}
            <OptionSwatch
              label="Select version:"
              selectedValue={selectedMode}
              setSelectedValue={setSelectedMode as (val: string) => void}
              options={[
                {
                  value: 'DRAFT',
                  title: 'Draft / latest version',
                  tooltip: 'Copy draft, or the latest version if no draft is open.',
                },
                {
                  value: 'VERSION',
                  title: (
                    <>
                      Latest
                      <br />
                      version
                    </>
                  ),
                  tooltip: 'Copy the latest version.',
                },
                {
                  value: 'APPROVED',
                  title: 'Approved version',
                  tooltip: 'Only copy the latest approved version.',
                },
              ].filter(
                (option) =>
                  !state?.allowedPreferences || state.allowedPreferences.includes(option.value as TVersionPreference)
              )}
              className="mb-4"
            />
          </DialogContent>
          <DialogActions>
            <ButtonLoading
              appearance="primary"
              icon={state?.buttonIcon || <DocumentArrowDownRegular />}
              onClick={handleSelect}
              loading={bulkExport.isPending || bulkExportSubmission.isPending}
            >
              {state?.buttonLabel || 'Pull'}
            </ButtonLoading>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Cancel</Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default VersionPreferenceDialog;
